import { BTaxCart } from '@/app/modules/btax/services/btax-cart.service';
import { selectBTaxAccount, selectBTaxAccountHistory, selectBTaxAccountInCart, selectBTaxAccountIsPayable } from '@/app/modules/btax/state/entities/account/btax-account.selectors';
import { selectBTaxBill } from '@/app/modules/btax/state/entities/bill/btax-bill.selectors';
import { Component, Input } from '@angular/core';
import { selectBTaxBlockMessages, selectBTaxDisplayPrintBillButton, selectBTaxDisplayReceiptId } from '@btax/state/btax-state/btax-state.selectors';
import { sync } from '@ces/sync';
import { Viewport } from '@egovsolutions/angular-viewport';
import { Store } from '@ngrx/store';
import { UndefinedLists } from '@shared/services/undefined-lists.service';
import { State } from '@state/model';
import { BTaxBill, BTaxBillPayment } from 'egov-api';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

// eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
@Component({
    selector: 'btax-account-history',
    templateUrl: './btax-account-history.component.html',
    styleUrls: ['./btax-account-history.component.scss']
})
export class BTaxAccountHistoryComponent
{
    constructor
    (
        // Dependencies

        private readonly store: Store<State>,
        private readonly btaxCartService: BTaxCart,


        // Template dependencies

        protected readonly undefinedLists: UndefinedLists,
        protected readonly viewport: Viewport,
    )
    {
        this.historyBillsAndPaymentsTrackBy = this.historyBillsAndPaymentsTrackBy.bind(this);
    }


    // Input properties

    @Input() protected set accountNumber(value: string | undefined) { this.accountNumber$.next(value); }
    private readonly accountNumber$ = new BehaviorSubject<string | undefined>(undefined);

    @Input() protected loaded?: boolean;


    // Service state / settings

    protected readonly displayReceiptId$ = this.store.select(selectBTaxDisplayReceiptId);
    protected readonly displayPrintBillButton$ = this.store.select(selectBTaxDisplayPrintBillButton);
    protected readonly blockMessages$ = this.store.select(selectBTaxBlockMessages);


    // BTax Entities

    protected readonly account$ = this.accountNumber$.pipe(
        switchMap(accountNumber => accountNumber
            ? this.store.select(selectBTaxAccount(accountNumber))
            : of(undefined)
        )
    );


    // Details / Payments expanded

    private readonly billsShowingDetailsFor: Set<string> = new Set<string>();
    protected readonly billsWithPaymentsExpanded$ = new BehaviorSubject(new Set<string>());

    protected showingDetails(bill: BTaxBill): boolean { return bill && this.billsShowingDetailsFor.has(bill.id); }

    protected toggleDetailsForBill(bill: BTaxBill)
    {
        if (bill)
            if (!this.billsShowingDetailsFor.has(bill.id))
                this.billsShowingDetailsFor.add(bill.id);
            else
                this.billsShowingDetailsFor.delete(bill.id);
    }


    // History

    protected readonly history$: Observable<(BTaxBill | undefined)[] | undefined> = this.accountNumber$.pipe(
        switchMap(accountNumber => accountNumber
            ? this.store.select(selectBTaxAccountHistory(accountNumber)).pipe(
                switchMap(billIds => billIds
                    ? combineLatest(billIds.map(id => this.store.select(selectBTaxBill(id)))).pipe(
                        map(bills => bills.sort((a, b) => a && b ? b.taxYear - a.taxYear : 0)),
                    )
                    : of(undefined)
                )
            )
            : of(undefined)
        ),
    );

    protected historyTrackBy(index: number, item: undefined | BTaxBill): number | string
    {
        if (item === undefined) return `${index}`;
        return `bill-${item.id}`;
    }

    protected readonly historyBillsAndPayments$ =
        combineLatest([this.history$, this.billsWithPaymentsExpanded$]).pipe(
            map(([bills, billsWithPaymentsExpanded]) => bills?.reduce(
                (out, bill) => bill && billsWithPaymentsExpanded.has(bill.id) && bill.payments
                    ? [...out, bill, ...bill.payments]
                    : [...out, bill],
                [] as (BTaxBill | BTaxBillPayment | undefined)[]
            )),
        );

    protected historyBillsAndPaymentsTrackBy(index: number, item: undefined | BTaxBill | BTaxBillPayment): string
    {
        if (item === undefined) return `${index}`;
        return this.isBill(item) ? `bill-${item.id}` : `payment-${item.receipt}`;
    }

    private typeOfItem(item: BTaxBill | BTaxBillPayment): string
    {
        return !item || 'accountNumber' in item ? 'bill' : 'payment';
    }

    protected isBill(item: BTaxBill | BTaxBillPayment): item is BTaxBill
    {
        return this.typeOfItem(item) === 'bill';
    }

    protected isPayment(item: BTaxBill | BTaxBillPayment): item is BTaxBillPayment
    {
        return this.typeOfItem(item) === 'payment';
    }


    // Cart

    protected readonly totalAccountDue$ = this.account$.pipe(map(account => account?.due?.total));

    protected readonly addToOrRemoveFromCartTitle$ = this.accountNumber$.pipe(
        switchMap(accountNumber => accountNumber
            ? this.btaxCartService.getAddToOrRemoveFromCartTitle$(accountNumber)
            : of(undefined)
        )
    );

    protected readonly accountIsPayable$ = this.accountNumber$.pipe(
        switchMap(accountNumber => accountNumber
            ? this.store.select(selectBTaxAccountIsPayable(accountNumber))
            : of(undefined)
        )
    );

    protected readonly accountInCart$ = this.accountNumber$.pipe(
        switchMap(accountNumber => accountNumber
            ? this.store.select(selectBTaxAccountInCart(accountNumber))
            : of(undefined)
        ),
    );

    protected readonly updatingAccountCartStatus$ = new BehaviorSubject(false);

    protected toggleAccountInCart()
    {
        if (this.accountNumber$.value && !this.updatingAccountCartStatus$.value)
        {
            this.updatingAccountCartStatus$.next(true);
            if (!sync(this.account$)?.paymentBlocked)
                this.btaxCartService.toggleAccountInCart(this.accountNumber$.value, true).subscribe({
                    complete: () => this.updatingAccountCartStatus$.next(false),
                    error: () => this.updatingAccountCartStatus$.next(false),
                });
        }
    }


    // Receipt

    protected getPrintReceiptTitle(bill: BTaxBill): string | undefined
    {
        if (bill)
        {
            if (bill.payments?.length)
            {
                if (bill.payments.length === 1)
                    return this.getPaymentPrintReceiptTitle(bill.payments[0]);
                else
                    return 'This bill has multiple payments. Click to expand them.';
            }
            else
            {
                return 'This bill doesn\'t have any receipts yet.';
            }
        }
    }

    protected getPaymentPrintReceiptTitle(payment: BTaxBillPayment): string
    {
        if (payment.printUrl)
            return 'Print Receipt';
        else
            return 'A receipt cannot be printed for this payment.';
    }

    protected shouldBillHaveItsReceiptButtonDisabled(bill: BTaxBill): boolean
    {
        return (
        !(
            bill?.payments
            &&
            (
                bill.payments.length === 1
                &&
                !!bill.payments[0].printUrl
                ||
                bill.payments.length > 1
            )
        ));
    }

    protected receiptsToggleHandler(e: Event, bill: BTaxBill)
    {
        e.stopPropagation();

        const clonedSet = new Set(this.billsWithPaymentsExpanded$.value);

        if (!clonedSet.has(bill.id))
        {
            clonedSet.add(bill.id);
            this.billsWithPaymentsExpanded$.next(clonedSet);
        }
        else
        {
            clonedSet.delete(bill.id);
            this.billsWithPaymentsExpanded$.next(clonedSet);
        }
    }


    protected billReceiptButtonClickHandler(e: Event, bill: BTaxBill)
    {
        e.stopPropagation();

        if (bill.payments?.length)
        {
            if (bill.payments.length === 1)
                this.paymentReceiptButtonClickHandler(bill.payments[0]);
            else
            {
                const clonedSet = new Set(this.billsWithPaymentsExpanded$.value);

                if (!clonedSet.has(bill.id))
                {
                    clonedSet.add(bill.id);
                    this.billsWithPaymentsExpanded$.next(clonedSet);
                }
                else
                {
                    clonedSet.delete(bill.id);
                    this.billsWithPaymentsExpanded$.next(clonedSet);
                }
            }
        }
    }

    protected paymentReceiptButtonClickHandler(payment: BTaxBillPayment, e?: Event)
    {
        if (e) e.stopPropagation();

        if (payment.printUrl)
            window.open(payment.printUrl);
    }
}
